import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, PO_STATUS, MAT_TYPE } from "@/lib/ui";
import { toast } from "sonner";
import { Plus, PackageCheck, Trash2, X, Truck, Search, Pencil, Ban } from "lucide-react";

export default function PurchaseOrders() {
  const { isEditor } = useAuth();
  const [pos, setPos] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [horizon, setHorizon] = useState([]);
  const [modal, setModal] = useState(false);
  const [matSearch, setMatSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [flt, setFlt] = useState({ material: "", supplier: "", siparis: "", gelen: "", bekleyen: "", varis: "", durum: "" });
  const [receivePo, setReceivePo] = useState(null);
  const [edit, setEdit] = useState(null);
  const [editForm, setEditForm] = useState({ supplier: "", quantity_ordered: "", order_week: "", expected_week: "" });
  const [recQty, setRecQty] = useState("");
  const [recIrs, setRecIrs] = useState("");
  const [recAnl, setRecAnl] = useState("");
  const [form, setForm] = useState({ material_id: "", supplier: "", quantity_ordered: "", order_week: "", expected_week: "" });

  const load = () => {
    api.get("/purchase-orders").then((r) => setPos(r.data));
    api.get("/materials").then((r) => setMaterials(r.data));
    api.get("/projection?weeks=12").then((r) => setHorizon(r.data.horizon));
  };
  useEffect(load, []);

  const mat = (id) => materials.find((m) => m.id === id);
  const selMat = mat(form.material_id);

  const matResults = useMemo(() => {
    if (!matSearch) return materials.slice(0, 30);
    return materials.filter((m) => m.name.toLowerCase().includes(matSearch.toLowerCase())).slice(0, 30);
  }, [materials, matSearch]);

  const openNew = () => {
    setForm({ material_id: "", supplier: "", quantity_ordered: "", order_week: horizon[0]?.label || "", expected_week: "" });
    setMatSearch("");
    setModal(true);
  };

  const save = async () => {
    if (!form.material_id || !form.supplier || !form.quantity_ordered || !form.expected_week) {
      toast.error("Tüm alanları doldurun"); return;
    }
    try {
      await api.post("/purchase-orders", { ...form, quantity_ordered: parseFloat(form.quantity_ordered) });
      toast.success("Sipariş oluşturuldu");
      setModal(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doReceive = async () => {
    try {
      await api.post(`/purchase-orders/${receivePo.id}/receive`, {
        quantity: parseFloat(recQty), irsaliye_no: recIrs.trim(), anlasma_no: recAnl.trim(),
      });
      toast.success("Teslimat kaydedildi, stok güncellendi");
      setReceivePo(null); setRecQty(""); setRecIrs(""); setRecAnl(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => { if (!window.confirm("Bu siparişi silmek istediğinize emin misiniz? Alınan miktar stoktan geri düşülür.")) return; await api.delete(`/purchase-orders/${id}`); load(); };
  const closePo = async (id) => {
    try { await api.post(`/purchase-orders/${id}/close`); toast.success("Sipariş kapatıldı"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openEdit = (po) => {
    setEdit(po);
    setEditForm({ supplier: po.supplier, quantity_ordered: String(po.quantity_ordered), order_week: po.order_week, expected_week: po.expected_week });
  };
  const saveEdit = async () => {
    try {
      await api.put(`/purchase-orders/${edit.id}`, {
        supplier: editForm.supplier, quantity_ordered: parseFloat(editForm.quantity_ordered),
        order_week: editForm.order_week, expected_week: editForm.expected_week,
      });
      toast.success("Sipariş güncellendi"); setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-in-up" data-testid="po-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight">Satın Alma Siparişleri</h1>
          <p className="text-[#94A3B8] text-sm mt-1">Sipariş durumu, gelen ve bekleyen miktarlar</p>
        </div>
        {isEditor && (
          <button data-testid="purchase-order-create-modal-trigger" onClick={openNew}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-sm font-semibold">
            <Plus className="w-4 h-4" /> Yeni Sipariş
          </button>
        )}
      </div>

      <div className="flex gap-1.5 flex-wrap items-center" data-testid="po-type-tabs">
        {[
          { k: "", l: "Tümü" }, { k: "tel", l: "Tel" }, { k: "tela", l: "Tela" },
          { k: "kraft", l: "Kraft" }, { k: "lamet", l: "Lamet" }, { k: "cring", l: "C-Ring" },
          { k: "holtmelt", l: "Holtmelt" }, { k: "celik_cember", l: "Çelik Çember" },
          { k: "tahta_palet", l: "Tahta Palet" }, { k: "round_frame", l: "Round Frame" },
        ].map((t) => (
          <button key={t.k} data-testid={`po-type-${t.k || "all"}`} onClick={() => setTypeFilter(t.k)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              typeFilter === t.k ? "bg-[#2563EB] text-white" : "bg-[#131A26] border border-[#212D40] text-[#94A3B8] hover:text-white"
            }`}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="bg-[#131A26] border border-[#212D40] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="po-table">
            <thead>
              <tr className="text-left text-[#94A3B8] text-xs uppercase tracking-wider border-b border-[#212D40] bg-[#0F1520]">
                <th className="py-3 px-5">Malzeme</th>
                <th className="py-3 px-5">Tedarikçi</th>
                <th className="py-3 px-5 text-right">Sipariş</th>
                <th className="py-3 px-5 text-right">Gelen</th>
                <th className="py-3 px-5 text-right">Bekleyen</th>
                <th className="py-3 px-5">Varış</th>
                <th className="py-3 px-5">Durum</th>
                <th className="py-3 px-5"></th>
              </tr>
              <tr className="border-b border-[#212D40] bg-[#0F1520]" data-testid="po-filter-row">
                <td className="py-1.5 px-3"><input data-testid="po-filter-material" placeholder="Malzeme ara..." value={flt.material} onChange={(e) => setFlt({ ...flt, material: e.target.value })} className="w-full bg-[#131A26] border border-[#212D40] rounded px-2 py-1 text-xs outline-none focus:border-[#2563EB]" /></td>
                <td className="py-1.5 px-3">
                  <select data-testid="po-filter-supplier" value={flt.supplier} onChange={(e) => setFlt({ ...flt, supplier: e.target.value })} className="w-full bg-[#131A26] border border-[#212D40] rounded px-2 py-1 text-xs outline-none focus:border-[#2563EB]">
                    <option value="">Tümü</option>
                    {[...new Set(pos.map((p) => p.supplier).filter(Boolean))].sort().map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="py-1.5 px-3"><input data-testid="po-filter-siparis" placeholder="≈" value={flt.siparis} onChange={(e) => setFlt({ ...flt, siparis: e.target.value })} className="w-full bg-[#131A26] border border-[#212D40] rounded px-2 py-1 text-xs outline-none focus:border-[#2563EB] text-right font-num" /></td>
                <td className="py-1.5 px-3"><input data-testid="po-filter-gelen" placeholder="≈" value={flt.gelen} onChange={(e) => setFlt({ ...flt, gelen: e.target.value })} className="w-full bg-[#131A26] border border-[#212D40] rounded px-2 py-1 text-xs outline-none focus:border-[#2563EB] text-right font-num" /></td>
                <td className="py-1.5 px-3"><input data-testid="po-filter-bekleyen" placeholder="≈" value={flt.bekleyen} onChange={(e) => setFlt({ ...flt, bekleyen: e.target.value })} className="w-full bg-[#131A26] border border-[#212D40] rounded px-2 py-1 text-xs outline-none focus:border-[#2563EB] text-right font-num" /></td>
                <td className="py-1.5 px-3">
                  <select data-testid="po-filter-varis" value={flt.varis} onChange={(e) => setFlt({ ...flt, varis: e.target.value })} className="w-full bg-[#131A26] border border-[#212D40] rounded px-2 py-1 text-xs outline-none focus:border-[#2563EB]">
                    <option value="">Tümü</option>
                    {[...new Set(pos.map((p) => p.expected_week).filter(Boolean))].sort().map((w) => <option key={w} value={w}>{w}</option>)}
                  </select>
                </td>
                <td className="py-1.5 px-3">
                  <select data-testid="po-filter-durum" value={flt.durum} onChange={(e) => setFlt({ ...flt, durum: e.target.value })} className="w-full bg-[#131A26] border border-[#212D40] rounded px-2 py-1 text-xs outline-none focus:border-[#2563EB]">
                    <option value="">Tümü</option>
                    {Object.entries(PO_STATUS).filter(([k]) => k !== "kapali").map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </td>
                <td className="py-1.5 px-3"></td>
              </tr>
            </thead>
            <tbody>
              {[...pos].sort((a, b) => String(a.expected_week || "").localeCompare(String(b.expected_week || ""), undefined, { numeric: true })).map((po) => {
                const m = mat(po.material_id);
                const outstanding = po.quantity_ordered - po.quantity_received;
                if (typeFilter && m?.type !== typeFilter) return null;
                if (po.status === "kapali") return null;
                if (po.quantity_received >= po.quantity_ordered) return null;
                if (flt.material && !(m?.name || "").toLowerCase().includes(flt.material.toLowerCase())) return null;
                if (flt.supplier && po.supplier !== flt.supplier) return null;
                if (flt.siparis && !String(po.quantity_ordered).includes(flt.siparis)) return null;
                if (flt.gelen && !String(po.quantity_received).includes(flt.gelen)) return null;
                if (flt.bekleyen && !String(outstanding).includes(flt.bekleyen)) return null;
                if (flt.varis && po.expected_week !== flt.varis) return null;
                if (flt.durum && po.status !== flt.durum) return null;
                const s = PO_STATUS[po.status] || PO_STATUS.bekliyor;
                return (
                  <tr key={po.id} className="border-b border-[#1a2334] hover:bg-[#0F1520]" data-testid={`po-row-${po.id}`}>
                    <td className="py-3 px-5 font-medium">{m?.name || "—"}</td>
                    <td className="py-3 px-5 text-[#94A3B8]">{po.supplier}</td>
                    <td className="py-3 px-5 text-right font-num">{fmt(po.quantity_ordered)} {m?.unit}
                    </td>
                    <td className="py-3 px-5 text-right font-num text-[#10B981]">{fmt(po.quantity_received)}
                    </td>
                    <td className="py-3 px-5 text-right font-num text-[#F59E0B]">{fmt(outstanding)}
                    </td>
                    <td className="py-3 px-5 font-num">{po.expected_week}</td>
                    <td className="py-3 px-5">
                      <span data-testid="purchase-order-status-badge" className="text-xs font-semibold px-2.5 py-1 rounded-full"
                        style={{ color: s.color, background: `${s.color}22`, border: `1px solid ${s.color}55` }}>{s.label}</span>
                    </td>
                    <td className="py-3 px-5 text-right whitespace-nowrap">
                      {isEditor && po.status !== "teslim_alindi" && (
                        <button data-testid={`po-receive-${po.id}`} onClick={() => { setReceivePo(po); setRecQty(String(outstanding)); }}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-[rgba(16,185,129,0.15)] text-[#10B981] hover:bg-[rgba(16,185,129,0.25)] mr-2">
                          <PackageCheck className="w-3.5 h-3.5" /> Teslim Al
                        </button>
                      )}
                      {isEditor && (
                        <button data-testid={`po-edit-${po.id}`} onClick={() => openEdit(po)}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-[#131A26] border border-[#212D40] text-[#94A3B8] hover:text-white mr-2">
                          <Pencil className="w-3.5 h-3.5" /> Düzenle
                        </button>
                      )}
                      {isEditor && (
                        <button data-testid={`po-close-${po.id}`} onClick={() => closePo(po.id)}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-[rgba(100,116,139,0.15)] text-[#94A3B8] hover:bg-[rgba(100,116,139,0.25)] mr-2">
                          <Ban className="w-3.5 h-3.5" /> Kapat
                        </button>
                      )}
                      {isEditor && (
                        <button data-testid={`po-delete-${po.id}`} onClick={() => remove(po.id)} className="text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {pos.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-[#64748B]">Sipariş yok.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#131A26] border border-[#212D40] rounded-xl w-full max-w-lg max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-[#212D40]">
              <h3 className="font-head text-xl font-semibold">Yeni Satın Alma Siparişi</h3>
              <button onClick={() => setModal(false)}><X className="w-5 h-5 text-[#94A3B8]" /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div>
                <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Malzeme</label>
                {selMat ? (
                  <div className="mt-1 flex items-center justify-between bg-[#0F1520] border border-[#2563EB] rounded-lg px-3 py-2 text-sm">
                    <span>{selMat.name}</span>
                    <button onClick={() => setForm({ ...form, material_id: "", supplier: "" })} className="text-[#94A3B8] hover:text-white"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <>
                    <div className="relative mt-1">
                      <Search className="w-4 h-4 text-[#64748B] absolute left-3 top-1/2 -translate-y-1/2" />
                      <input data-testid="po-material-search" placeholder="Malzeme ara (örn Tel 1.4, Tela Beyaz 50)..." value={matSearch}
                        onChange={(e) => setMatSearch(e.target.value)}
                        className="w-full bg-[#0F1520] border border-[#212D40] rounded-lg pl-10 pr-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                    </div>
                    <div className="mt-1 max-h-40 overflow-y-auto border border-[#212D40] rounded-lg divide-y divide-[#1a2334]">
                      {matResults.map((m) => {
                        const t = MAT_TYPE[m.type] || {};
                        return (
                          <button key={m.id} data-testid={`po-material-option-${m.id}`}
                            onClick={() => setForm({ ...form, material_id: m.id, supplier: "" })}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-[#131A26] flex items-center gap-2">
                            <span style={{ color: t.color }}>●</span> {m.name}
                            <span className="ml-auto text-xs text-[#64748B]">{fmt(m.current_stock)} {m.unit}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Tedarikçi (Termin)</label>
                <input data-testid="po-form-supplier" list="po-supplier-list" value={form.supplier}
                  onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                  placeholder={(selMat?.suppliers || []).length ? "Seçin veya yazın" : "Tedarikçi adı yazın"}
                  className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                <datalist id="po-supplier-list">
                  {(selMat?.suppliers || []).map((s) => <option key={s.name} value={s.name} />)}
                </datalist>
                {selMat && (selMat.suppliers || []).length === 0 && (
                  <div className="text-xs text-[#64748B] mt-1">Tanımlı tedarikçi yok — doğrudan yazabilirsiniz.</div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Miktar</label>
                  <input data-testid="po-form-qty" type="number" value={form.quantity_ordered} onChange={(e) => setForm({ ...form, quantity_ordered: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Sipariş Hf</label>
                  <select value={form.order_week} onChange={(e) => setForm({ ...form, order_week: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]">
                    {horizon.map((h) => <option key={h.label} value={h.label}>{h.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Varış Hf</label>
                  <select data-testid="po-form-expected" value={form.expected_week} onChange={(e) => setForm({ ...form, expected_week: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]">
                    <option value="">Seçin</option>
                    {horizon.map((h) => <option key={h.label} value={h.label}>{h.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-[#212D40]">
              <button onClick={() => setModal(false)} className="px-4 py-2 rounded-lg text-sm text-[#94A3B8] hover:bg-[#0F1520]">İptal</button>
              <button data-testid="po-form-save" onClick={save} className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#2563EB] hover:bg-[#1D4ED8]">Sipariş Ver</button>
            </div>
          </div>
        </div>
      )}

      {receivePo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#131A26] border border-[#212D40] rounded-xl w-full max-w-sm">
            <div className="flex items-center gap-2 p-5 border-b border-[#212D40]">
              <Truck className="w-5 h-5 text-[#10B981]" />
              <h3 className="font-head text-xl font-semibold">Teslimat Kaydet</h3>
            </div>
            <div className="p-5">
              <p className="text-sm text-[#94A3B8] mb-3">
                {mat(receivePo.material_id)?.name} — Bekleyen:{" "}
                <b className="text-white font-num">{fmt(receivePo.quantity_ordered - receivePo.quantity_received)}</b>
              </p>
              <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Gelen Miktar</label>
              <input data-testid="po-receive-qty" type="number" value={recQty} onChange={(e) => setRecQty(e.target.value)}
                className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">İrsaliye No</label>
                  <input data-testid="po-receive-irsaliye" value={recIrs} onChange={(e) => setRecIrs(e.target.value)}
                    placeholder="örn IRS-2026-014"
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Anlaşma No</label>
                  <input data-testid="po-receive-anlasma" value={recAnl} onChange={(e) => setRecAnl(e.target.value)}
                    placeholder="örn ANL-8821"
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-[#212D40]">
              <button onClick={() => setReceivePo(null)} className="px-4 py-2 rounded-lg text-sm text-[#94A3B8] hover:bg-[#0F1520]">İptal</button>
              <button data-testid="po-receive-confirm" onClick={doReceive} className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#10B981] hover:bg-[#059669]">Onayla</button>
            </div>
          </div>
        </div>
      )}
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#131A26] border border-[#212D40] rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-[#212D40]">
              <h3 className="font-head text-xl font-semibold">Siparişi Düzenle</h3>
              <button onClick={() => setEdit(null)}><X className="w-5 h-5 text-[#94A3B8]" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-[#94A3B8]">{mat(edit.material_id)?.name}</p>
              <div>
                <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Tedarikçi</label>
                <select data-testid="po-edit-supplier" value={editForm.supplier} onChange={(e) => setEditForm({ ...editForm, supplier: e.target.value })}
                  className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]">
                  {[...new Set([editForm.supplier, ...((mat(edit.material_id)?.suppliers || []).map((s) => s.name))].filter(Boolean))].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Miktar (tonaj)</label>
                  <input data-testid="po-edit-qty" type="number" value={editForm.quantity_ordered} onChange={(e) => setEditForm({ ...editForm, quantity_ordered: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Sipariş Hf</label>
                  <select value={editForm.order_week} onChange={(e) => setEditForm({ ...editForm, order_week: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]">
                    {[...new Set([editForm.order_week, ...horizon.map((h) => h.label)].filter(Boolean))].map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Varış Hf (ertele)</label>
                  <select data-testid="po-edit-expected" value={editForm.expected_week} onChange={(e) => setEditForm({ ...editForm, expected_week: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]">
                    {[...new Set([editForm.expected_week, ...horizon.map((h) => h.label)].filter(Boolean))].map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-[#212D40]">
              <button onClick={() => setEdit(null)} className="px-4 py-2 rounded-lg text-sm text-[#94A3B8] hover:bg-[#0F1520]">İptal</button>
              <button data-testid="po-edit-save" onClick={saveEdit} className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#2563EB] hover:bg-[#1D4ED8]">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
